import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Loader2, CheckCircle2, FileText, Download, ShieldCheck } from "lucide-react";
import { LogoLockup } from "./Logo";
import SignaturePad from "./SignaturePad";

const inputClass =
    "w-full h-11 px-3.5 bg-white border-2 border-black/10 focus:border-[#FF6B1A] outline-none rounded-none text-[14px] transition-colors";

export default function ISOAgreementModal({ open, onClose, prefill = {}, leadId = "" }) {
    const [step, setStep] = useState(1); // 1 = info, 2 = bank, 3 = sign
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [agreementId, setAgreementId] = useState("");

    const [form, setForm] = useState({
        legal_name: prefill.business_name || "",
        dba: "",
        address: "",
        ein: "",
        bank_name: "",
        bank_account: "",
        bank_routing: "",
        owner_name: prefill.name || "",
        owner_cell: prefill.phone || "",
        owner_email: prefill.email || "",
        signatory_name: prefill.name || "",
        signatory_title: "",
        signature_data_url: "",
        agreed: false,
    });

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const onCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

    if (!open) return null;

    const close = () => {
        setStep(1);
        setDone(false);
        setForm((f) => ({ ...f, signature_data_url: "", agreed: false }));
        onClose();
    };

    const validateStep = () => {
        if (step === 1) {
            if (!form.legal_name || !form.address || !form.ein || !form.owner_name || !form.owner_cell || !form.owner_email) {
                toast.error("Please fill all required fields");
                return false;
            }
        }
        if (step === 2) {
            if (!form.bank_name || !form.bank_account || !form.bank_routing) {
                toast.error("Please complete bank/ACH information");
                return false;
            }
            if (form.bank_routing.replace(/\D/g, "").length !== 9) {
                toast.error("Routing number must be 9 digits");
                return false;
            }
        }
        return true;
    };

    const next = () => { if (validateStep()) setStep(step + 1); };
    const back = () => setStep(Math.max(1, step - 1));

    const submit = async () => {
        if (!form.signatory_name || !form.signatory_title) {
            toast.error("Please enter signatory name and title");
            return;
        }
        if (!form.signature_data_url) {
            toast.error("Please sign in the signature box");
            return;
        }
        if (!form.agreed) {
            toast.error("Please agree to the terms");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/iso-agreement", { ...form, lead_id: leadId });
            setAgreementId(data.id);
            setDone(true);
            toast.success("Agreement signed and saved");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Submission failed";
            toast.error(typeof detail === "string" ? detail : "Could not submit");
        } finally {
            setLoading(false);
        }
    };

    const Step = ({ n, label }) => (
        <div className="flex items-center gap-3">
            <div className={`w-7 h-7 flex items-center justify-center font-display font-bold text-sm border-2 ${
                step > n ? "bg-[#FF6B1A] border-[#FF6B1A] text-white"
                : step === n ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                : "bg-white border-black/15 text-black/45"
            }`}>{step > n ? "✓" : n}</div>
            <span className={`text-xs uppercase tracking-[0.14em] font-semibold ${step >= n ? "text-black" : "text-black/40"}`}>{label}</span>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="iso-agreement-modal">
            <div className="bg-white w-full max-w-3xl border-2 border-[#0A0A0A] shadow-[12px_12px_0_0_#FF6B1A] my-8">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black/10 bg-[#F8F5F2]">
                    <div className="flex items-center gap-4">
                        <LogoLockup size={32} />
                        <div className="hidden md:block">
                            <div className="eyebrow">Step {Math.min(step, 3)} of 3</div>
                            <div className="font-display font-bold">ISO Partner Agreement</div>
                        </div>
                    </div>
                    <button onClick={close} className="text-black/50 hover:text-black p-2" data-testid="iso-modal-close" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                {!done && (
                    <div className="px-6 md:px-10 pt-6">
                        <div className="flex items-center gap-6 flex-wrap pb-5 border-b border-black/10">
                            <Step n={1} label="Business Info" />
                            <div className="hidden sm:block flex-1 h-px bg-black/15" />
                            <Step n={2} label="Bank / ACH" />
                            <div className="hidden sm:block flex-1 h-px bg-black/15" />
                            <Step n={3} label="Sign" />
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="px-6 md:px-10 py-6 max-h-[68vh] overflow-y-auto">
                    {done ? (
                        <div className="text-center py-10" data-testid="iso-agreement-success">
                            <CheckCircle2 className="mx-auto text-[#FF6B1A]" size={64} />
                            <h3 className="font-display text-3xl font-bold mt-5">Agreement signed.</h3>
                            <p className="text-black/65 mt-3 max-w-md mx-auto">
                                A countersigned copy will be emailed to <strong>{form.owner_email}</strong>. Welcome to the
                                Lended ISO network. Your funding specialist will reach out within 1 business hour.
                            </p>
                            <a href="/downloads/Lended-ISO-Agreement.pdf" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#FF6B1A] hover:underline" data-testid="download-blank-agreement">
                                <Download size={14} /> Download a blank copy of the agreement
                            </a>
                            <div className="mt-8">
                                <button onClick={close} className="btn-black" data-testid="iso-modal-finish">Done</button>
                            </div>
                        </div>
                    ) : step === 1 ? (
                        <div data-testid="iso-step-1">
                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">ISO Legal Name *</label>
                                    <input required value={form.legal_name} onChange={onChange("legal_name")} placeholder="ABC Capital Group LLC" className={inputClass} data-testid="iso-legal-name" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">DBA (if any)</label>
                                    <input value={form.dba} onChange={onChange("dba")} placeholder="Optional" className={inputClass} data-testid="iso-dba" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">EIN *</label>
                                    <input required value={form.ein} onChange={onChange("ein")} placeholder="XX-XXXXXXX" className={inputClass} data-testid="iso-ein" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Business Address *</label>
                                    <input required value={form.address} onChange={onChange("address")} placeholder="Street, City, State, ZIP" className={inputClass} data-testid="iso-address" />
                                </div>
                                <div className="md:col-span-2 mt-2 pt-4 border-t border-black/10">
                                    <div className="eyebrow mb-3">Owner / Contact</div>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Owner Name *</label>
                                    <input required value={form.owner_name} onChange={onChange("owner_name")} className={inputClass} data-testid="iso-owner-name" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Owner Cell *</label>
                                    <input required value={form.owner_cell} onChange={onChange("owner_cell")} className={inputClass} data-testid="iso-owner-cell" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Owner Email *</label>
                                    <input required type="email" value={form.owner_email} onChange={onChange("owner_email")} className={inputClass} data-testid="iso-owner-email" />
                                </div>
                            </div>
                        </div>
                    ) : step === 2 ? (
                        <div data-testid="iso-step-2">
                            <div className="bg-[#F8F5F2] p-4 border-l-4 border-[#FF6B1A] mb-5 flex items-start gap-3">
                                <ShieldCheck className="text-[#FF6B1A] shrink-0 mt-0.5" size={20} />
                                <div className="text-sm text-black/75">
                                    Your bank info is used <strong>only</strong> for ACH commission deposits. We mask the
                                    full numbers in our system once stored.
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Bank Name *</label>
                                    <input required value={form.bank_name} onChange={onChange("bank_name")} className={inputClass} data-testid="iso-bank-name" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Account Number *</label>
                                    <input required value={form.bank_account} onChange={onChange("bank_account")} className={inputClass} data-testid="iso-bank-account" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Routing Number * (9 digits)</label>
                                    <input required value={form.bank_routing} onChange={onChange("bank_routing")} className={inputClass} data-testid="iso-bank-routing" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div data-testid="iso-step-3">
                            <div className="grid md:grid-cols-2 gap-3 mb-6">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Authorized Signatory Name *</label>
                                    <input required value={form.signatory_name} onChange={onChange("signatory_name")} className={inputClass} data-testid="iso-signatory-name" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-1 block">Title *</label>
                                    <input required placeholder="Owner / President / Managing Member" value={form.signatory_title} onChange={onChange("signatory_title")} className={inputClass} data-testid="iso-signatory-title" />
                                </div>
                            </div>

                            <label className="text-xs uppercase tracking-wider font-semibold text-black/60 mb-2 block">Signature *</label>
                            <SignaturePad onChange={(d) => setForm((f) => ({ ...f, signature_data_url: d }))} />

                            <label className="flex items-start gap-3 mt-6 text-xs text-black/70 leading-relaxed cursor-pointer" data-testid="iso-agree-label">
                                <input
                                    type="checkbox"
                                    checked={form.agreed}
                                    onChange={onCheck("agreed")}
                                    className="mt-0.5 w-4 h-4 accent-[#FF6B1A]"
                                    data-testid="iso-agree-checkbox"
                                />
                                <span>
                                    By signing above and checking this box, I confirm I am authorized to bind the named
                                    Independent Sales Organization to the <strong>Lended ISO Agreement</strong>{" "}
                                    (<a href="/downloads/Lended-ISO-Agreement.pdf" target="_blank" rel="noreferrer" className="text-[#FF6B1A] underline">view full agreement</a>),
                                    and I agree to all of its terms including confidentiality, non-solicitation,
                                    non-circumvention, commission structure, and indemnification provisions. This electronic
                                    signature has the same legal effect as a handwritten signature pursuant to the E-SIGN Act.
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!done && (
                    <div className="px-6 md:px-10 py-4 border-t-2 border-black/10 bg-[#F8F5F2] flex justify-between items-center gap-4">
                        <button
                            type="button"
                            onClick={step === 1 ? close : back}
                            className="text-sm font-semibold text-black/70 hover:text-black inline-flex items-center gap-2"
                            data-testid="iso-back-btn"
                        >
                            <ArrowLeft size={16} /> {step === 1 ? "Cancel" : "Back"}
                        </button>
                        {step < 3 ? (
                            <button type="button" onClick={next} className="btn-orange text-sm" data-testid="iso-next-btn">
                                Continue <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button type="button" onClick={submit} disabled={loading} className="btn-orange text-sm disabled:opacity-60" data-testid="iso-submit-btn">
                                {loading ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                                {loading ? "Signing..." : "Sign Agreement"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
